import { useCallback, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export interface DailyGoal {
  providerId: string;
  providerVideaId?: string;
  providerName: string;
  date: string;
  goal: number;
  scheduledProduction: number;
  completedProduction: number;
  updatedBy?: string;
  updatedByName?: string;
  updatedAt?: string;
}

const progressPercent = (g: DailyGoal): number =>
  g.goal > 0 ? (g.scheduledProduction / g.goal) * 100 : 0;

/** Sort goals best-performing first (descending by scheduled % of goal). */
const sortDailyGoalsByProgress = (goals: DailyGoal[]): DailyGoal[] =>
  [...goals].sort((a, b) => progressPercent(b) - progressPercent(a));

export interface GetDailyGoalsResponse {
  data: DailyGoal[];
}

interface GetDailyGoalsVariables {
  date: Date | string | null;
  providerIds?: string[];
  practiceId?: string;
}

const REFETCH_INTERVAL_IN_SECONDS = 30;
const MIN_TOTAL_SCHEDULED_PRODUCTION = 200;

// Helper to check if provider matches based on Videa provider id
const providerMatches = (goal: DailyGoal, providerIds?: string[]): boolean =>
  !providerIds?.length ||
  (goal.providerVideaId != null && providerIds.includes(goal.providerVideaId));

// Backend "scheduledProduction" is "remaining scheduled". Convert to total scheduled
// (completed + remaining) for display, progress %, and color coding.
const totalScheduledProduction = (goal: DailyGoal): number =>
  (goal.completedProduction ?? 0) + (goal.scheduledProduction ?? 0);

// Hide providers below the minimum scheduled total for the day.
const hasScheduledTotal = (goal: DailyGoal): boolean =>
  totalScheduledProduction(goal) >= MIN_TOTAL_SCHEDULED_PRODUCTION;

/**
 * Fetches daily goals for the selected date/practice.
 * Filters by selected Videa provider ids, removes goals with total scheduled production under 200,
 * and sorts by scheduled progress (highest first).
 */
export const useGetDailyGoals = ({
  date,
  providerIds,
  practiceId,
}: GetDailyGoalsVariables) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  const effectivePracticeId = practiceId || currentPracticeId;

  const queryFn = useCallback(async () => {
    if (!date) return { data: [] };

    return await makeAPIRequestReactQuery<GetDailyGoalsResponse>(
      endpoints.dailyGoals,
      {
        date,
      },
      'GET',
      {
        headers: {
          org: currentOrganizationId,
          practice: effectivePracticeId,
        },
      },
    );
  }, [currentOrganizationId, date, effectivePracticeId]);

  const result = useQuery<GetDailyGoalsResponse>({
    queryKey: [endpoints.dailyGoals, effectivePracticeId, date],
    queryFn: queryFn,
    enabled: !!currentOrganizationId && !!effectivePracticeId && !!date,
    refetchInterval: REFETCH_INTERVAL_IN_SECONDS * 1000,
  });

  const filteredAndSortedGoals = useMemo(() => {
    if (!result.data?.data) return [];

    const normalizedGoals = result.data.data
      .filter(hasScheduledTotal)
      .map((goal) => ({
        ...goal,
        scheduledProduction: totalScheduledProduction(goal),
      }));

    let filtered = [...normalizedGoals];

    if (providerIds && providerIds.length > 0) {
      filtered = filtered.filter((goal) => providerMatches(goal, providerIds));
    }

    return sortDailyGoalsByProgress(filtered);
  }, [providerIds, result.data]);

  return { filteredAndSortedGoals, result };
};

export interface UpdateDailyGoalsVariables {
  date: Date | string;
  updates: { providerId: string; goal: number }[];
}

/**
 * Mutation to create or update daily goals. Used by Set Daily Goal modal on save.
 */
export const useUpdateDailyGoals = () => {
  const queryClient = useQueryClient();
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useMutation<void, Error, UpdateDailyGoalsVariables>({
    mutationFn: async ({ date, updates }) => {
      if (updates.length === 0) return;

      const startDate = typeof date === 'string' ? date : date.toISOString();

      await Promise.all(
        updates.map(({ providerId, goal }) =>
          makeAPIRequestReactQuery<void>(endpoints.dailyGoals, {}, 'PUT', {
            data: {
              providerId: Number(providerId),
              dailyGoalAmount: goal,
              startDate,
            },
            headers: {
              org: currentOrganizationId,
              practice: currentPracticeId,
            },
          }),
        ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.dailyGoals] });
    },
  });
};
